import React from "react";
import { type LocationControlResult } from "@grnhse/location-control";
import { useLocationControl } from "@hooks/use_location_control";
import LocationIcon from "@sprout/icons/location";
import useTranslation from "@hooks/use_translation";
import { useSharedReactSelectProps } from "@sprout/select/internal/hooks/use_shared_react_select_props";
import { SelectWrapper } from "@sprout/select/internal/select_wrapper";
import ReactSelect from "react-select";
import { useAsyncReactSelectProps } from "@sprout/select/internal/hooks/use_async_react_select_props";

export type Location = {
  display_name: string;
  latitude: number;
  longitude: number;
  // There are users who have a null value from before we started collecting this field.
  // This field is also missing when Job Search loads with a location from the query string.
  country_short_name?: string | null;
  state_short_name?: string | null;
  location_type?: string | null;
};

type SharedProps = {
  value: Location | null;
  onChange: (newValue: Location | null) => void;
  helperText?: string;
  label?: string;
  placeholder?: string;
  icon?: React.ReactNode;
  granularLocationSearchEnabled?: boolean;
};

type Props = Omit<SharedProps, "label" | "placeholder" | "icon">;

const SharedLocationInput = ({
  value,
  onChange,
  helperText,
  label,
  placeholder,
  icon,
  granularLocationSearchEnabled,
}: SharedProps) => {
  const { t } = useTranslation("user");
  const locationControl = useLocationControl(granularLocationSearchEnabled || false);

  const selectedOption = value ? { value: 0, label: value.display_name } : undefined;
  const placeholderText = icon ? t("fields.location") : placeholder;

  const { id, ...sharedAsyncReactSelectProps } = useSharedReactSelectProps(
    async (selectedLocation) => {
      if (!selectedLocation) {
        return onChange(null);
      }

      const result = selectedLocation as LocationControlResult;
      const newValue = await locationControl.resultSelected(result);

      if (!newValue) {
        return onChange(null);
      }

      onChange({
        display_name: newValue.displayName,
        latitude: newValue.latitude,
        longitude: newValue.longitude,
        country_short_name: newValue.country_short_name,
        state_short_name: newValue.state_short_name,
        location_type: result.layer || null,
      });
    },
    {
      hideDropdownIndicator: true,
      hideNoOptionsMessage: true,
      label,
      startIcon: icon,
    },
  );

  const asyncReactSelectProps = useAsyncReactSelectProps(async (search) => {
    if (!search || search.trim().length === 0) {
      return [];
    }

    return locationControl.results(search);
  });

  return (
    <SelectWrapper selectId={id} label={label} helperText={helperText}>
      <ReactSelect
        id={id}
        {...sharedAsyncReactSelectProps}
        isClearable
        placeholder={placeholderText}
        value={selectedOption}
        {...asyncReactSelectProps}
      />
    </SelectWrapper>
  );
};

export const LocationInput = (props: Props) => {
  const { t } = useTranslation("user");

  return (
    <SharedLocationInput
      {...props}
      placeholder={t("placeholder.location")}
      label={t("fields.location")}
    />
  );
};

export const IconLocationInput = (props: Props) => {
  return <SharedLocationInput {...props} icon={<LocationIcon />} />;
};
